/**
 * The dictation models behind the dictation Engine menu
 * (`use-dictation-engine-menu.ts`): every catalogue model with its size and
 * state, how far a download is, and the user's choice.
 *
 * Rust owns the downloads (`dictation_models.rs`), so one started by
 * onboarding or at startup keeps going while Settings is closed. A model
 * already downloading when this mounts, or reporting progress while it is
 * mounted, is joined - a second download call waits for the first, and tries
 * once more if the first failed - so the list learns when it ends. Read again
 * when the window regains focus; there is no polling.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  dictationModels,
  downloadDictationModel,
  onDictationModelProgress,
  removeDictationModel,
  selectDictationModel,
  type DictationModelId,
  type DictationModels,
} from "./dictation-models";
import { percentComplete } from "./voice-assets";

const message = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

export type DictationModelsView = {
  /** `null` outside the desktop app, or until the first answer. */
  models: DictationModels | null;
  /** How far each model this window follows has downloaded. */
  percent: Readonly<Record<DictationModelId, number>>;
  /** Models whose last download failed. */
  failed: readonly DictationModelId[];
  /** Why the last choice or removal did not happen. */
  error: string | null;
  /** Make the model the choice and, unless it is installed, download it;
   *  answers whether it became the choice. */
  choose: (model: DictationModelId) => Promise<boolean>;
  remove: (model: DictationModelId) => Promise<void>;
};

export function useDictationModels(): DictationModelsView {
  const [models, setModels] = useState<DictationModels | null>(null);
  const [percent, setPercent] = useState<Record<DictationModelId, number>>({});
  const [failed, setFailed] = useState<DictationModelId[]>([]);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  const following = useRef(new Set<DictationModelId>());

  const settle = useCallback((next: DictationModels | null) => {
    if (mounted.current) setModels(next);
  }, []);
  const refresh = useCallback(
    () => void dictationModels().then(settle),
    [settle],
  );

  useEffect(() => {
    mounted.current = true;
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      mounted.current = false;
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  /** Start or join `model`'s download and take the list it answers. */
  const follow = useCallback(
    (model: DictationModelId) => {
      if (following.current.has(model)) return;
      following.current.add(model);
      setFailed((current) => current.filter((id) => id !== model));
      setPercent((current) => ({ ...current, [model]: current[model] ?? 0 }));
      void downloadDictationModel(model)
        .then(settle, () => {
          if (mounted.current)
            setFailed((current) => [...new Set([...current, model])]);
          refresh();
        })
        .finally(() => {
          following.current.delete(model);
          if (!mounted.current) return;
          setPercent((current) => {
            const next = { ...current };
            delete next[model];
            return next;
          });
        });
    },
    [refresh, settle],
  );

  // A download already running, or one that starts elsewhere while this is
  // mounted (startup, onboarding), is joined to learn when it ends.
  useEffect(() => {
    for (const model of models?.models ?? []) {
      if (model.state === "downloading") follow(model.id);
    }
  }, [models, follow]);
  useEffect(
    () =>
      onDictationModelProgress((progress) => {
        follow(progress.model);
        setPercent((current) => ({
          ...current,
          [progress.model]: percentComplete(
            progress.downloaded_bytes,
            progress.total_bytes,
          ),
        }));
      }),
    [follow],
  );

  const choose = useCallback(
    async (model: DictationModelId) => {
      setError(null);
      try {
        const next = await selectDictationModel(model);
        settle(next);
        const status = next.models.find((entry) => entry.id === model);
        if (status && status.state !== "installed") follow(model);
        return true;
      } catch (reason) {
        if (mounted.current) setError(message(reason));
        return false;
      }
    },
    [follow, settle],
  );

  const remove = useCallback(
    async (model: DictationModelId) => {
      setError(null);
      try {
        settle(await removeDictationModel(model));
      } catch (reason) {
        if (mounted.current) setError(message(reason));
      }
    },
    [settle],
  );

  return { models, percent, failed, error, choose, remove };
}
